import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const CONFETTI_COLORS = ['#ff4d6d', '#ffd166', '#06d6a0', '#4cc9f0', '#b5179e', '#f77f00'];
const BALLOONS = [
  { left: '6%', color: '#ff4d6d', delay: '0s', size: 54 },
  { left: '20%', color: '#ffd166', delay: '1.2s', size: 46 },
  { left: '78%', color: '#4cc9f0', delay: '0.6s', size: 52 },
  { left: '90%', color: '#06d6a0', delay: '1.8s', size: 44 },
];

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

const BirthdayPopup = () => {
  const { user } = useAuth();
  const uid = user?._id || user?.id;
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);

  // Random confetti, generated once
  const confetti = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 4}s`,
    duration: `${3 + Math.random() * 3}s`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    round: i % 3 === 0,
  })), []);

  // Shown once per calendar day per user, regardless of how many times they log in.
  useEffect(() => {
    if (!uid) return undefined;
    const key = `bdayPopup:${uid}:${new Date().toDateString()}`;
    let cancelled = false;

    if (!localStorage.getItem(key)) {
      api.get('/birthdays/today')
        .then(({ data }) => {
          if (cancelled) return;
          localStorage.setItem(key, '1');
          if (data.birthdays?.length) {
            setPeople(data.birthdays);
            setOpen(true);
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const me = people.find((p) => String(p.id) === String(uid));
  const others = people.filter((p) => String(p.id) !== String(uid));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm bday-fade">
      <style>{`
        @keyframes bdayFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes bdayPop { 0% { transform: scale(.5) translateY(30px); opacity: 0 } 100% { transform: scale(1) translateY(0); opacity: 1 } }
        @keyframes bdayBounce { 0%,100% { transform: translateY(0) rotate(-4deg) } 50% { transform: translateY(-14px) rotate(4deg) } }
        @keyframes bdayFall { 0% { transform: translateY(-20px) rotate(0); opacity: 1 } 100% { transform: translateY(560px) rotate(720deg); opacity: 0 } }
        @keyframes bdayRise { 0% { transform: translateY(40px); opacity: 0 } 15% { opacity: 1 } 100% { transform: translateY(-420px) } }
        @keyframes bdayShine { 0% { background-position: 0% 50% } 100% { background-position: 200% 50% } }
        .bday-fade { animation: bdayFade .35s ease-out }
        .bday-card { animation: bdayPop .6s cubic-bezier(.2,1.3,.4,1) }
        .bday-cake { animation: bdayBounce 1.6s ease-in-out infinite }
        .bday-title { background: linear-gradient(90deg,#fff,#ffe29a,#fff,#ffb3c6,#fff); background-size: 200% auto; -webkit-background-clip: text; background-clip: text; color: transparent; animation: bdayShine 4s linear infinite }
      `}</style>

      <div className="bday-card relative w-full max-w-md overflow-hidden rounded-[2rem] bg-gradient-to-br from-pink-500 via-fuchsia-500 to-indigo-600 px-6 pb-8 pt-10 text-center text-white shadow-2xl">
        {/* confetti */}
        {confetti.map((c, i) => (
          <span key={i} className="pointer-events-none absolute -top-3"
            style={{
              left: c.left, width: 9, height: c.round ? 9 : 14, background: c.color,
              borderRadius: c.round ? '50%' : 2, opacity: 0.9,
              animation: `bdayFall ${c.duration} linear ${c.delay} infinite`,
            }} />
        ))}
        {/* balloons */}
        {BALLOONS.map((b, i) => (
          <span key={i} className="pointer-events-none absolute bottom-0"
            style={{
              left: b.left, width: b.size, height: b.size * 1.2, background: b.color,
              borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%', opacity: 0.85,
              boxShadow: 'inset -6px -8px 0 rgba(0,0,0,.12)',
              animation: `bdayRise 6s ease-in ${b.delay} infinite`,
            }} />
        ))}

        <button onClick={() => setOpen(false)} aria-label="Close"
          className="absolute right-4 top-4 z-10 rounded-full bg-white/20 p-1.5 text-white transition hover:bg-white/30">
          <X className="h-5 w-5" />
        </button>

        <div className="relative z-10">
          <div className="bday-cake text-6xl drop-shadow-lg">🎂</div>
          <h2 className="bday-title mt-3 text-4xl font-extrabold tracking-tight">Happy Birthday!</h2>

          {me ? (
            <>
              <p className="mt-3 text-xl font-semibold">{me.name}</p>
              <p className="mt-2 text-sm text-white/90">
                Wishing you a year full of joy, success and wonderful moments. Have a fantastic day! 🎉
              </p>
            </>
          ) : (
            <p className="mt-3 text-sm text-white/90">
              Today we celebrate {people.length === 1 ? 'a teammate' : 'our teammates'}. Take a moment to send your wishes! 🎈
            </p>
          )}

          {(me ? others : people).length > 0 && (
            <div className="mt-5">
              {me && <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-white/80">Also celebrating today</p>}
              <div className="mx-auto flex max-h-44 flex-wrap justify-center gap-3 overflow-y-auto">
                {(me ? others : people).map((p) => (
                  <div key={p.id} className="flex items-center gap-2.5 rounded-full bg-white/20 py-1.5 pl-1.5 pr-4 backdrop-blur">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-bold text-fuchsia-600">
                      {initials(p.name)}
                    </span>
                    <span className="text-left leading-tight">
                      <span className="block text-sm font-semibold">{p.name}</span>
                      {p.jobTitle && <span className="block text-[11px] text-white/80">{p.jobTitle}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => setOpen(false)}
            className="mt-7 rounded-full bg-white px-7 py-2.5 text-sm font-bold text-fuchsia-600 shadow-lg transition hover:scale-105">
            {me ? 'Thank you! 🎁' : 'Send Wishes 🎉'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BirthdayPopup;